import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query and re-renders when it starts or stops matching.
 *
 * `window.matchMedia` is absent in jsdom unless stubbed (the designer's test setup
 * stubs it to never match), and Safari before 14 only implements the deprecated
 * `addListener`/`removeListener` pair, so both are handled here rather than at the
 * call sites.
 */
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>((): boolean => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(query).matches;
  });

  useEffect((): (() => void) | undefined => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQueryList: MediaQueryList = window.matchMedia(query);

    const handleChange = (event: MediaQueryListEvent | MediaQueryList): void => {
      setMatches(event.matches);
    };

    // The query may already have flipped between the initial state and this effect.
    handleChange(mediaQueryList);

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange);

      return (): void => {
        mediaQueryList.removeEventListener("change", handleChange);
      };
    }

    mediaQueryList.addListener(handleChange);

    return (): void => {
      mediaQueryList.removeListener(handleChange);
    };
  }, [query]);

  return matches;
};

export default useMediaQuery;
